// src/pages/accounts-receivable-management-dashboard/index.jsx

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import NavigationHeader from "../../components/ui/NavigationHeader";
import ReceivableFilterBar from "./components/ReceivableFilterBar";
import ReceivableKPIs from "./components/ReceivableKPIs";
import OverdueClientsTable from "./components/OverdueClientsTable";
import CampaignPerformanceCard from "./components/CampaignPerformanceCard";
import BillingAutomationSummaryCard from "./components/BillingAutomationSummaryCard";
import Button from "../../components/ui/Button";
import Icon from "../../components/AppIcon";
import { fetchReceber } from "./services/receberApi";

function useDebouncedEffect(effect, deps, delay = 350) {
  const first = useRef(true);

  useEffect(() => {
    if (first.current) {
      first.current = false;
      effect();
      return;
    }

    const t = setTimeout(() => effect(), delay);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}

const isoToday = () => new Date().toISOString().slice(0, 10);
const startOfMonthISO = () => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  return `${y}-${m}-01`;
};

const AccountsReceivableManagementDashboard = () => {
  const todayISO = isoToday();
  const monthStartISO = startOfMonthISO();

  // ✅ O FilterBar trabalha com 1 objeto "value" + "onChange"
  const [barValue, setBarValue] = useState({
    base: "ALL", // ALL | A | B
    de: monthStartISO,
    ate: todayISO,
    situacao: "TODOS", // TODOS | ABERTO | BAIXADO | CANCELADO
    risco: "TODOS", // (não usado no back hoje, mas pode ficar)
    periodoPreset: "MES",
    q: "",
  });

  const [isLoading, setIsLoading] = useState(false);
  const [payload, setPayload] = useState(null);
  const [error, setError] = useState(null);

  const effectiveQuery = useMemo(() => {
    return {
      de: barValue.de,
      ate: barValue.ate,
      base: barValue.base,
      situacao: barValue.situacao,
      q: barValue.q,
      risco: barValue.risco,
    };
  }, [barValue]);

  const load = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const data = await fetchReceber(effectiveQuery);

      if (data && data.ok === false) {
        setPayload(data);
        setError({
          statusCode: data.statusCode || 400,
          message: data.message || "Falha ao carregar contas a receber.",
        });
        return;
      }

      setPayload(data);
    } catch (e) {
      const msg = e?.message || "Falha ao carregar contas a receber.";
      setError({ statusCode: e?.statusCode || 500, message: msg });
      setPayload(null);
    } finally {
      setIsLoading(false);
    }
  }, [effectiveQuery]);

  // ✅ Atualiza automaticamente quando trocar base/data/situação/busca
  useDebouncedEffect(
    () => load(),
    [barValue.base, barValue.de, barValue.ate, barValue.situacao, barValue.q, barValue.risco],
    250
  );

  const analytics = payload?.analytics || null;
  const itens = payload?.itens || [];

  return (
    <div className="min-h-screen bg-background">
      <NavigationHeader />

      <main className="max-w-7xl mx-auto px-4 pb-8 pt-24">
        <div className="bg-card rounded-lg border border-border p-4 card-shadow mb-5">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div>
              <h1 className="font-heading-semibold text-xl text-card-foreground">
                Contas a Receber
              </h1>
              <p className="text-sm text-muted-foreground">
                Controle de recebimentos, cobrança e inadimplência
              </p>
            </div>

            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={load} disabled={isLoading}>
                <Icon name="RefreshCw" size={16} />
                <span className="ml-2">{isLoading ? "Atualizando..." : "Atualizar"}</span>
              </Button>

              <Button variant="primary" size="sm" disabled>
                <Icon name="Receipt" size={16} />
                <span className="ml-2">Emitir boleto (BB)</span>
              </Button>
            </div>
          </div>

          {error && (
            <div className="mt-3 rounded-lg border border-border bg-accent/30 p-3">
              <div className="flex items-start gap-3">
                <div className="mt-0.5">
                  <Icon name="AlertCircle" size={18} />
                </div>
                <div className="flex-1">
                  <p className="font-body-medium text-sm text-card-foreground">
                    Não foi possível carregar os dados
                  </p>
                  <p className="text-sm text-muted-foreground mt-1">
                    {error.message} {error.statusCode ? `(HTTP ${error.statusCode})` : ""}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* ✅ Agora está conectado certo */}
        <ReceivableFilterBar
          value={barValue}
          onChange={setBarValue}
          onRefresh={load}
          isLoading={isLoading}
        />

        <ReceivableKPIs analytics={analytics} items={itens} isLoading={isLoading} />

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mt-5">
          <div className="lg:col-span-2">
            <OverdueClientsTable items={itens} />
          </div>

          <div className="lg:col-span-1">
            <BillingAutomationSummaryCard />
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mt-5">
          <div className="bg-card rounded-lg border border-border p-4 card-shadow">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-heading-semibold text-base text-card-foreground">
                Boletos (Banco do Brasil)
              </h3>
              <span className="text-[11px] px-2 py-1 rounded-full bg-muted/40 text-muted-foreground">
                Depende do BB
              </span>
            </div>
            <p className="text-sm text-muted-foreground">
              Emissão de boletos via integração bancária. Aqui entra autenticação, convênio,
              parâmetros do título, PDF e webhook/consulta de situação.
            </p>

            <div className="mt-3 flex gap-2">
              <Button variant="outline" size="sm" disabled>
                <Icon name="FileText" size={16} />
                <span className="ml-2">Gerar boleto</span>
              </Button>
              <Button variant="outline" size="sm" disabled>
                <Icon name="Search" size={16} />
                <span className="ml-2">Consultar título</span>
              </Button>
            </div>
          </div>

          <div className="bg-card rounded-lg border border-border p-4 card-shadow">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-heading-semibold text-base text-card-foreground">
                Remessa / Retorno (CNAB)
              </h3>
              <span className="text-[11px] px-2 py-1 rounded-full bg-muted/40 text-muted-foreground">
                Depende do BB
              </span>
            </div>

            <p className="text-sm text-muted-foreground">
              Geração de arquivo de remessa e leitura do retorno para baixa automática.
              Integração com layout CNAB do BB + armazenamento no banco + conciliação.
            </p>

            <div className="mt-3 flex gap-2">
              <Button variant="outline" size="sm" disabled>
                <Icon name="Upload" size={16} />
                <span className="ml-2">Importar retorno</span>
              </Button>
              <Button variant="outline" size="sm" disabled>
                <Icon name="Download" size={16} />
                <span className="ml-2">Gerar remessa</span>
              </Button>
            </div>
          </div>
        </div>

        <div className="mt-5">
          <CampaignPerformanceCard />
        </div>

      </main>
    </div>
  );
};

export default AccountsReceivableManagementDashboard;
